import { STORAGE_KEYS, LOCAL_ME } from './constants';
import suggestedMockData from './suggestedMockData.json';
import { getCurrentFormattedTime } from '../Chat/util';

const safeParse = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (e) {
    return fallback;
  }
};

const read = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  return safeParse(window.localStorage.getItem(key), fallback);
};

const write = (key, value) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
};

export const isWingLocalLoggedIn = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEYS.auth) === '1';
};

export const setWingLocalLoggedIn = (loggedIn) => {
  if (typeof window === 'undefined') return;
  if (loggedIn) {
    window.localStorage.setItem(STORAGE_KEYS.auth, '1');
  } else {
    window.localStorage.removeItem(STORAGE_KEYS.auth);
  }
};

export const clearWingLocalSession = () => {
  if (typeof window === 'undefined') return;
  Object.values(STORAGE_KEYS).forEach((key) => {
    window.localStorage.removeItem(key);
  });
};

export const normalizeSuggestedUser = (item) => {
  const { status, tags, template, settings, ...data } = item || {};
  return data;
};

export const getSuggestedUsersFromConst = () => {
  const users = suggestedMockData?.data?.users || [];
  return users
    .map(normalizeSuggestedUser)
    .filter((item) => item?.name && item?.pictures?.[0])
    .slice(0, 20);
};

export const loadWingPlaygroundState = () => {
  const likedUsers = read(STORAGE_KEYS.likedUsers, []);
  const conversations = read(STORAGE_KEYS.conversations, []);
  const messages = read(STORAGE_KEYS.messages, []);
  const swipedIds = read(STORAGE_KEYS.swipedIds, []);

  return {
    userInfo: LOCAL_ME,
    likedUsers,
    conversations,
    messages,
    swipedIds,
    suggestedUsers: getSuggestedUsersFromConst().filter((user) => !swipedIds.includes(user.id)),
  };
};

export const persistWingPlaygroundState = ({ likedUsers, conversations, messages, swipedIds }) => {
  write(STORAGE_KEYS.likedUsers, likedUsers || []);
  write(STORAGE_KEYS.conversations, conversations || []);
  write(STORAGE_KEYS.messages, messages || []);
  write(STORAGE_KEYS.swipedIds, swipedIds || []);
};

const createConversation = (userId, messageIds = [], latestTime = getCurrentFormattedTime()) => ({
  id: userId,
  owner: {
    id: LOCAL_ME.id,
    type: 'user',
  },
  otherUser: {
    id: userId,
    type: 'user',
  },
  messages: {
    count: messageIds.length,
    ids: messageIds,
    unread: 0,
  },
  read: true,
  readUntil: '',
  otherReadUntil: '',
  clearedUntil: '-1',
  createdTime: latestTime,
  latestTime,
  type: 'conversation',
  unreadMessages: 0,
  todayPick: null,
  updatedTime: latestTime,
  status: 'default',
  matchType: 'swipe',
  pinned: false,
});

export const applyLocalLike = ({ user, likedUsers, conversations, messages, swipedIds }) => {
  if (!user?.id) {
    return { likedUsers, conversations, messages, swipedIds };
  }

  const nextSwipedIds = swipedIds.includes(user.id) ? swipedIds : [...swipedIds, user.id];
  const alreadyLiked = likedUsers.some((item) => item.id === user.id);
  if (alreadyLiked) {
    return {
      likedUsers,
      conversations,
      messages,
      swipedIds: nextSwipedIds,
    };
  }

  const now = getCurrentFormattedTime();
  return {
    likedUsers: [user, ...likedUsers],
    conversations: [createConversation(user.id, [], now), ...conversations],
    messages,
    swipedIds: nextSwipedIds,
  };
};

export const applyLocalDislike = ({ userId, likedUsers, conversations, messages, swipedIds }) => {
  const nextSwipedIds = swipedIds.includes(userId) ? swipedIds : [...swipedIds, userId];
  return {
    likedUsers,
    conversations,
    messages,
    swipedIds: nextSwipedIds,
  };
};

export const applyLocalSendMessage = ({
  userId,
  value,
  likedUsers,
  conversations,
  messages,
}) => {
  const now = getCurrentFormattedTime();
  const message = {
    id: `local-message-${Date.now()}`,
    type: 'message',
    contentType: 'text',
    value,
    createdTime: now,
    matchType: 'swip',
    owner: {
      id: LOCAL_ME.id,
      type: 'user',
    },
    otherUser: {
      id: userId,
      type: 'user',
    },
  };

  const nextMessages = [message, ...messages];
  const existingConversation = conversations.find((item) => item.id === userId);
  let nextConversations;

  if (existingConversation) {
    const ids = [message.id, ...(existingConversation.messages?.ids || [])];
    nextConversations = conversations.map((item) => {
      if (item.id !== userId) return item;
      return {
        ...item,
        messages: {
          ...item.messages,
          count: ids.length,
          ids,
          unread: 0,
        },
        latestTime: now,
        updatedTime: now,
        unreadMessages: 0,
      };
    });
  } else {
    nextConversations = [createConversation(userId, [message.id], now), ...conversations];
  }

  // move active conversation to top
  nextConversations = [
    nextConversations.find((item) => item.id === userId),
    ...nextConversations.filter((item) => item.id !== userId),
  ].filter(Boolean);

  return {
    likedUsers,
    conversations: nextConversations,
    messages: nextMessages,
  };
};

export const markConversationReadLocal = ({ conversationId, conversations }) => {
  return conversations.map((item) => {
    if (item.id !== conversationId) return item;
    return {
      ...item,
      unreadMessages: 0,
      read: true,
    };
  });
};
